// Epic Error Detector — scans tool invocations on the three Prisma task
// bots for Epic-side failure codes (INVALID-DATE, NO-MYCHART-FOUND,
// INVALID-PROVIDER-ID, …) plus a catch-all for non-SUCCESS states.
//
// WHY this report exists:
//   Prisma Health runs on a thin middleware over Epic's Scheduling2017
//   and Scheduling2019 APIs. Epic surfaces failures as hyphenated
//   SCREAMING-KEBAB-CASE codes in the fulfillment response — "INVALID-
//   DATE" when the schedule window exceeds the 30-day cap, "NO-MYCHART-
//   FOUND" when the patient has no linked MyChart, "INVALID-PROVIDER-ID"
//   when the LLM re-serialised a padded Internal ID. Operations needs a
//   per-bot/per-tool rollup so they can triage: a spike in INVALID-DATE
//   on ScheduleAppointment means the window-retry pattern regressed; a
//   spike in NO-MYCHART-FOUND is a demographic thing, not a bot bug.
//
// SIGNALS used:
//   * metadata.tools[].state   — "SUCCESS" vs. anything else
//   * metadata.tools[].output  — Epic error codes land here as substrings
//
// GAPS:
//   * We don't match on nested JSON structure — a code only lands if it's
//     a substring of the stringified output. In practice Epic emits these
//     at the top of the response, so substring is reliable.
//   * We don't try to attribute an Epic error to a specific caller /
//     patient — that's a privacy risk and this report is about trends.
//   * NO-MYCHART-FOUND isn't really a bug — it's an expected outcome for
//     callers without MyChart. Surfacing it anyway because a sudden spike
//     usually means the specialty filter changed or the middleware cache
//     is stale.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Group, Progress, Stack, Text, Title } from '@mantine/core';
import {
  IconAlertTriangle,
  IconInfoCircle,
  IconPlayerStopFilled,
  IconSparkles,
} from '@tabler/icons-react';

import { ReportTable } from './ReportTable';
import { SessionListModal } from '../components/SessionListModal';
import { callTool } from '../../../api_scripts/wxccMcpClient';
import { scanEpicErrors, toRows, DEFAULT_TARGET_BOTS } from '../lib/epicErrors';

const REPORT_ID = 'epic-error-detector';
// Cisco MCP cap: 1 req / ~2s. Stay just under the cap on each fetch so
// a long window doesn't trip rate-limit 429s mid-scan.
const RATE_GAP_MS = 2200;

const COLUMNS = [
  {
    id: 'bot',
    label: 'Bot',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.bot.replace(/^PROD-/, '')}</Text>,
  },
  {
    id: 'tool',
    label: 'Tool',
    pinned: true,
    render: (r) => <Text size="sm" style={{ fontFamily: 'monospace' }}>{r.tool}</Text>,
  },
  {
    id: 'errorType',
    label: 'Error type',
    pinned: true,
    render: (r) => <Badge size="sm" color={errorColor(r.errorType)} variant="light">{r.errorType}</Badge>,
  },
  {
    id: 'count',
    label: 'Count',
    align: 'right',
    render: (r) => <Text size="sm" fw={600}>{r.count}</Text>,
  },
  {
    id: 'totalInvocations',
    label: 'Total invocations',
    align: 'right',
    hint: 'Total invocations of this tool across the scanned sessions. Count/Total = error rate.',
    render: (r) => <Text size="sm" c="dimmed">{r.totalInvocations}</Text>,
  },
  {
    id: 'pct',
    label: '% of invocations',
    align: 'right',
    render: (r) => {
      const color = r.pct >= 25 ? 'red' : r.pct >= 10 ? 'orange' : r.pct > 0 ? 'yellow' : 'gray';
      return <Badge size="sm" color={color} variant="light">{r.pct}%</Badge>;
    },
  },
  {
    id: 'sessionsImpacted',
    label: 'Sessions impacted',
    align: 'right',
    hint: 'Distinct sessions where this error fired. Can be less than count if a tool retried.',
    render: (r) => <Text size="sm">{r.sessionIds.length}</Text>,
  },
];

const DEFAULT_VISIBLE = ['count', 'totalInvocations', 'pct', 'sessionsImpacted'];

function errorColor(errorType) {
  if (errorType.startsWith('INVALID')) return 'red';
  if (errorType === 'NO-MYCHART-FOUND') return 'orange';
  return 'yellow'; // OTHER (non-SUCCESS state)
}

export function EpicErrorDetector({ data, onViewSession, window }) {
  const sessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);
  const [agentIdByName, setAgentIdByName] = useState({});
  const [agentIdToName, setAgentIdToName] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const [drill, setDrill] = useState(null); // { title, sessions }

  // One-shot agent id map so the deep scan can call per-session messages.
  useEffect(() => {
    let cancelled = false;
    callTool('ops', 'wxcc-operations-list-ai-agents', { view: 'list' })
      .then((res) => {
        if (cancelled) return;
        const forward = {};
        const reverse = {};
        for (const a of res?.agents ?? []) {
          if (a.name && a.id) {
            forward[a.name] = a.id;
            reverse[a.id] = a.name;
          }
        }
        setAgentIdByName(forward);
        setAgentIdToName(reverse);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Count how many sessions are in-scope for the deep scan so we can show
  // the operator a realistic "this will take N minutes" preview before
  // they click the button.
  const candidateCount = useMemo(() => {
    const targetBots = new Set(DEFAULT_TARGET_BOTS);
    const resolve = (s) => s.agentName || (s.agentId && agentIdToName[s.agentId]) || '';
    return sessions.filter((s) => targetBots.has(resolve(s))).length;
  }, [sessions, agentIdToName]);
  const estimatedSeconds = Math.ceil((candidateCount * RATE_GAP_MS) / 1000);

  async function runScan() {
    if (running) return;
    setRunning(true);
    setError(null);
    setResult(null);
    setProgress({ done: 0, total: candidateCount });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const scanResult = await scanEpicErrors(sessions, {
        agentIdByName,
        agentIdToName,
        signal: controller.signal,
        onProgress: (done, total) => setProgress({ done, total }),
        fetchMessages: async (agentId, sessionId) => {
          // Rate-limit: sleep BEFORE every call so the first message
          // request doesn't fire inside the Cisco server's throttling
          // window from an earlier page load.
          await new Promise((r) => setTimeout(r, RATE_GAP_MS));
          if (controller.signal.aborted) throw new Error('aborted');
          return callTool('ops', 'wxcc-operations-get-ai-agent-session-messages', {
            agentId, sessionId, limit: 100,
          });
        },
      });
      setResult(scanResult);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  }

  function cancelScan() {
    abortRef.current?.abort();
    setRunning(false);
  }

  const rows = useMemo(() => (result ? toRows(result) : []), [result]);

  const csvRows = rows.map((r) => ({
    bot: r.bot,
    tool: r.tool,
    error_type: r.errorType,
    count: r.count,
    total_invocations: r.totalInvocations,
    pct_of_invocations: r.pct,
    sessions_impacted: r.sessionIds.length,
  }));

  // Click a row → open the modal with the subset of sessions that hit THIS
  // specific (bot, tool, errorType) combination.
  const onRowClick = (r) => {
    const idSet = new Set(r.sessionIds);
    setDrill({
      title: `${r.bot.replace(/^PROD-/, '')} · ${r.tool} · ${r.errorType}`,
      sessions: sessions.filter((s) => idSet.has(s.sessionId)),
    });
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={4}>Epic Error Detector</Title>
          <Text size="xs" c="dimmed">
            Deep-scans tool metadata on the Prisma task bots for Epic error codes (INVALID-DATE,
            NO-MYCHART-FOUND, …) and non-SUCCESS states. Rate-limited at 2.2s per session —
            run on-demand only.
          </Text>
        </div>
        <Group gap="xs">
          {!running && (
            <Button
              size="xs"
              variant="light"
              color="violet"
              leftSection={<IconSparkles size={14} />}
              onClick={runScan}
              disabled={candidateCount === 0 || Object.keys(agentIdByName).length === 0}
            >
              {result ? 'Re-run deep scan' : 'Deep scan'}
            </Button>
          )}
          {running && (
            <>
              <Text size="xs" c="dimmed">
                Scanning {progress.done}/{progress.total}
              </Text>
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconPlayerStopFilled size={14} />}
                onClick={cancelScan}
              >
                Cancel
              </Button>
            </>
          )}
          {result && !running && (
            <Badge color="violet" variant="light">Scanned {result.done} sessions</Badge>
          )}
        </Group>
      </Group>

      {!result && !running && (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={14} />}>
          <Text size="xs">
            {candidateCount} candidate session{candidateCount === 1 ? '' : 's'} in window across the
            Prisma task bots. Scan will take ~{estimatedSeconds}s at the Cisco MCP 2.2s/req cap.
          </Text>
        </Alert>
      )}

      {running && progress.total > 0 && (
        <Progress
          value={(progress.done / progress.total) * 100}
          size="sm"
          color="violet"
          striped
          animated
        />
      )}

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={14} />}>
          Deep scan failed: {error}
        </Alert>
      )}

      {result && (
        <ReportTable
          title={`Epic error rollup — ${rows.length} (bot, tool, error) combination${rows.length === 1 ? '' : 's'}`}
          subtitle="Per-invocation counts. One session can produce multiple errors if the bot retried a failed tool. Click any row to see the sessions impacted."
          reportId={REPORT_ID}
          columns={COLUMNS}
          defaultVisibleIds={DEFAULT_VISIBLE}
          rows={rows}
          emptyLabel="No Epic errors detected in the scanned sessions."
          onRowClick={onViewSession ? onRowClick : undefined}
          csvExport={{
            filename: `epic-errors-${new Date().toISOString().slice(0, 10)}.csv`,
            rows: csvRows,
          }}
        />
      )}

      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title ?? ''}
        subtitle={drill ? `${drill.sessions.length} session${drill.sessions.length === 1 ? '' : 's'} with this error` : ''}
        sessions={drill?.sessions ?? []}
        onViewSession={onViewSession}
        window={window}
      />
    </Stack>
  );
}
